import type { PhotoKind, SubmitJobInput } from "@agw/contracts";
import { createStore, del, get, set, values } from "idb-keyval";
import { ApiError, fieldIssues } from "../../lib/api";
import { api } from "../../lib/endpoints";
import type { UploadType } from "../../lib/image";
import { defaultUploader, type PhotoUploader, uploadPhoto } from "../../lib/photo-upload";

/**
 * Offline outbox. A submit that cannot reach the API is stored here with its photo bytes,
 * then replayed on `online`, on app start and on a timer. The item id IS the job's
 * clientRequestId, so a replay after a lost response returns the same job, never a second one.
 */

export interface OutboxPhoto {
  blob: Blob;
  contentType: UploadType;
  thumb?: Blob;
  kind: PhotoKind;
  /** Set once uploaded, so a later replay skips it. Cleared if the server lost the staged copy. */
  photoId?: string;
}

export interface OutboxItem {
  id: string;
  createdAt: string;
  /** The login that saved it. Only that login sees or sends it, so a shared phone never mixes claims. */
  ownerId?: string;
  payload: Omit<SubmitJobInput, "photos">;
  photos: OutboxPhoto[];
  attempts: number;
  /** Set when the server refused it (not a network problem). The user must decide. */
  needsReview?: { code: string; message: string; details?: unknown };
}

/** Thrown when the phone has no room left to save a claim offline. The message is for the rep. */
export class StorageFullError extends Error {
  constructor() {
    super("Phone storage is full. Free up space (delete old photos or videos), then tap Send again. Or send it when you have signal.");
    this.name = "StorageFullError";
  }
}

/** IndexedDB and localStorage both report a full disk with these names (Safari, Chrome, Firefox). */
export const isQuotaError = (err: unknown) =>
  typeof err === "object" && err !== null && /QuotaExceeded|NS_ERROR_DOM_QUOTA_REACHED/i.test(String((err as { name?: unknown }).name ?? ""));

export interface KV {
  get(key: string): Promise<OutboxItem | undefined>;
  set(key: string, value: OutboxItem): Promise<void>;
  del(key: string): Promise<void>;
  values(): Promise<OutboxItem[]>;
}

export function idbKV(): KV {
  const store = createStore("agw-dealer", "outbox");
  return {
    get: (k) => get<OutboxItem>(k, store),
    set: (k, v) => set(k, v, store),
    del: (k) => del(k, store),
    values: () => values<OutboxItem>(store),
  };
}

export interface OutboxDeps {
  kv: KV;
  submitJob: (body: SubmitJobInput) => Promise<{ jobNumber: string; jobId: string }>;
  uploader: PhotoUploader;
}

/** signedOut: the session ended (signed out, or the login was switched off); claims wait for the next sign-in. */
export type ReplayResult = { sent: { id: string; jobNumber: string }[]; pending: number; stoppedOffline: boolean; signedOut?: boolean };

const mine = (item: OutboxItem, ownerId?: string) => !item.ownerId || !ownerId || item.ownerId === ownerId;

/** What the rep reads when the server refused a saved claim for good. Says how to save the claim. */
export function refusalNote(err: unknown): { code: string; message: string; details?: unknown } {
  if (!(err instanceof ApiError)) return { code: "unknown", message: String(err) };
  if (err.status === 403) {
    return { code: err.code, message: "You no longer have access to this dealership, so this claim did not send. Call the Auto Glass World office with the details below before you discard it.", details: err.details };
  }
  if (/store is not active|dealership is switched off/i.test(err.message)) {
    return { code: "store_inactive", message: "This dealership is switched off in the portal, so this claim did not send. Call the Auto Glass World office with the details below before you discard it.", details: err.details };
  }
  return { code: err.code, message: err.message, details: err.details };
}

export function createOutbox(deps: OutboxDeps) {
  const listeners = new Set<() => void>();
  let items: OutboxItem[] = [];
  let running: Promise<ReplayResult> | null = null;

  const emit = () => listeners.forEach((l) => l());
  const refresh = async () => {
    items = (await deps.kv.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    emit();
  };

  async function enqueue(payload: Omit<SubmitJobInput, "photos">, photos: OutboxPhoto[], ownerId?: string) {
    const item: OutboxItem = { id: payload.clientRequestId, createdAt: new Date().toISOString(), ownerId, payload, photos, attempts: 0 };
    try {
      await deps.kv.set(item.id, item);
    } catch (err) {
      if (isQuotaError(err)) throw new StorageFullError();
      throw err;
    }
    await refresh();
    return item;
  }

  async function sendOne(item: OutboxItem, opts: { acknowledgeDuplicate?: boolean } = {}): Promise<{ jobNumber: string }> {
    for (const photo of item.photos) {
      if (!photo.photoId) {
        photo.photoId = await uploadPhoto(photo.blob, photo.contentType, photo.kind, undefined, deps.uploader, undefined, photo.thumb);
        await deps.kv.set(item.id, item).catch(() => undefined); // remember progress so a later replay skips this photo; best effort
      }
    }
    const body: SubmitJobInput = {
      ...item.payload,
      ...(opts.acknowledgeDuplicate ? { acknowledgeDuplicate: true } : {}),
      photos: item.photos.map((p) => ({ photoId: p.photoId!, kind: p.kind })),
    };
    return deps.submitJob(body);
  }

  async function runReplay(ownerId?: string): Promise<ReplayResult> {
    const result: ReplayResult = { sent: [], pending: 0, stoppedOffline: false };
    const queue = (await deps.kv.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    for (const item of queue) {
      if (item.needsReview || !mine(item, ownerId)) continue;
      item.attempts += 1;
      try {
        const job = await sendOne(item);
        await deps.kv.del(item.id);
        result.sent.push({ id: item.id, jobNumber: job.jobNumber });
      } catch (err) {
        if (err instanceof ApiError && (err.isNetwork || err.status >= 500 || err.status === 429)) {
          await deps.kv.set(item.id, item);
          result.stoppedOffline = err.isNetwork;
          break; // keep order; try again later
        }
        // Session over (signed out elsewhere, or the login was switched off): keep every claim as it is.
        if (err instanceof ApiError && err.status === 401) {
          await deps.kv.set(item.id, item);
          result.signedOut = true;
          break;
        }
        // Staged photos expire after 24 h. If the server lost one, re-upload once and retry.
        if (err instanceof ApiError && err.code === "validation" && fieldIssues(err).some((i) => i.path.startsWith("photo")) && item.photos.some((p) => p.photoId)) {
          item.photos.forEach((p) => delete p.photoId);
          await deps.kv.set(item.id, item);
          continue;
        }
        item.needsReview = refusalNote(err);
        await deps.kv.set(item.id, item);
      }
    }
    await refresh();
    result.pending = items.length;
    return result;
  }

  return {
    enqueue,
    /** Single-flight: concurrent triggers share one run. Sends only `ownerId`'s claims. */
    replay(ownerId?: string): Promise<ReplayResult> {
      running ??= runReplay(ownerId).finally(() => {
        running = null;
      });
      return running;
    },
    /** User chose "Send anyway" on a duplicate-VIN item. */
    async sendAnyway(id: string) {
      const item = await deps.kv.get(id);
      if (!item) return;
      delete item.needsReview;
      const job = await sendOne(item, { acknowledgeDuplicate: true });
      await deps.kv.del(id);
      await refresh();
      return job;
    },
    async discard(id: string) {
      await deps.kv.del(id);
      await refresh();
    },
    refresh,
    subscribe(fn: () => void) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    snapshot: () => items,
    /** The claims one login saved on this phone. */
    itemsFor: (ownerId?: string) => items.filter((i) => mine(i, ownerId)),
  };
}

export type Outbox = ReturnType<typeof createOutbox>;

let singleton: Outbox | undefined;
export function getOutbox(): Outbox {
  singleton ??= createOutbox({ kv: idbKV(), submitJob: api.submitJob, uploader: defaultUploader });
  return singleton;
}
